import React, { useEffect, useState, useRef, useCallback } from "react";
import { Trash2, Plus, Edit2, Save, X, ChevronDown, ChevronUp } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import "./AdminShared.css";
import "./AdminAbout.css";
import { API_BASE_URL } from "../config";

const BASE = `${API_BASE_URL}/api/about`;

/* ─── helper: get token ───────────────────────────────────────────────────── */
function getToken() {
  try {
    const info = JSON.parse(localStorage.getItem("adminInfo"));
    return info?.token || null;
  } catch {
    return null;
  }
}

/* ─── Collapsible Section wrapper ────────────────────────────────────────── */
function Section({ title, badge, color = "#6366f1", children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="aa-section">
      <button className="aa-section-header" onClick={() => setOpen((v) => !v)}>
        <span className="aa-section-title">
          <span className="aa-section-dot" style={{ background: color }} />
          {title}
          {badge != null && (
            <span className="aa-section-badge" style={{ background: color }}>
              {badge}
            </span>
          )}
        </span>
        {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>
      {open && <div className="aa-section-body">{children}</div>}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
const AdminAbout = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("hero");

  // ── Hero Images ────────────────────────────────────────────────────────────
  const [heroImages, setHeroImages] = useState([]);
  const [heroFile, setHeroFile] = useState(null);
  const [heroAlt, setHeroAlt] = useState("");
  const [heroOrder, setHeroOrder] = useState(0);
  const [heroPreview, setHeroPreview] = useState(null);
  const heroInputRef = useRef(null);

  // ── Core Values ────────────────────────────────────────────────────────────
  const [coreValues, setCoreValues] = useState([]);
  const [cvForm, setCvForm] = useState({ icon: "🚀", title: "", description: "", order: 0 });
  const [cvEditing, setCvEditing] = useState(null);

  // ── Highlights ─────────────────────────────────────────────────────────────
  const [highlights, setHighlights] = useState([]);
  const [hlForm, setHlForm] = useState({ title: "", text: "", order: 0 });
  const [hlFile, setHlFile] = useState(null);
  const [hlPreview, setHlPreview] = useState(null);
  const [hlEditing, setHlEditing] = useState(null);
  const hlInputRef = useRef(null);

  // ── Facilities ─────────────────────────────────────────────────────────────
  const [facilities, setFacilities] = useState([]);
  const [facForm, setFacForm] = useState({ title: "", tag: "", points: "", order: 0 });
  const [facFile, setFacFile] = useState(null);
  const [facPreview, setFacPreview] = useState(null);
  const [facEditing, setFacEditing] = useState(null);
  const facInputRef = useRef(null);

  // ── DGCA Certs ─────────────────────────────────────────────────────────────
  const [dgcaCerts, setDgcaCerts] = useState([]);
  const [dgcaFile, setDgcaFile] = useState(null);
  const [dgcaAlt, setDgcaAlt] = useState("");
  const [dgcaPreview, setDgcaPreview] = useState(null);
  const dgcaInputRef = useRef(null);

  // ── Page Config ────────────────────────────────────────────────────────────
  const [config, setConfig] = useState(null);
  const [configSaving, setConfigSaving] = useState(false);

  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");

  /* Auth guard */
  useEffect(() => {
    const adminInfo = localStorage.getItem("adminInfo");
    if (!adminInfo) return navigate("/admin");
    try {
      const { token } = JSON.parse(adminInfo);
      const decoded = jwtDecode(token);
      if (decoded.exp * 1000 < Date.now()) {
        localStorage.removeItem("adminInfo");
        return navigate("/admin");
      }
    } catch {
      localStorage.removeItem("adminInfo");
      navigate("/admin");
    }
  }, [navigate]);

  /* Fetch all data */
  const fetchAll = useCallback(async () => {
    setLoading(true);
    const token = getToken();
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [hi, cv, hl, fac, dg, cfg] = await Promise.all([
        fetch(`${BASE}/hero-images`, { headers }).then((r) => r.json()),
        fetch(`${BASE}/core-values`, { headers }).then((r) => r.json()),
        fetch(`${BASE}/highlights`, { headers }).then((r) => r.json()),
        fetch(`${BASE}/facilities`, { headers }).then((r) => r.json()),
        fetch(`${BASE}/dgca-certs`, { headers }).then((r) => r.json()),
        fetch(`${BASE}/config`, { headers }).then((r) => r.json()),
      ]);
      setHeroImages(Array.isArray(hi) ? hi : []);
      setCoreValues(Array.isArray(cv) ? cv : []);
      setHighlights(Array.isArray(hl) ? hl : []);
      setFacilities(Array.isArray(fac) ? fac : []);
      setDgcaCerts(Array.isArray(dg) ? dg : []);
      setConfig(cfg || {});
    } catch (err) {
      console.error("Fetch error", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const toast = (msg) => {
    setStatus(msg);
    setTimeout(() => setStatus(""), 3000);
  };

  /* ── HERO: upload ────────────────────────────────────────────────────────── */
  const uploadHeroImage = async () => {
    if (!heroFile) return;
    const token = getToken();
    const fd = new FormData();
    fd.append("file", heroFile);
    fd.append("alt", heroAlt);
    fd.append("order", heroOrder);
    try {
      const r = await fetch(`${BASE}/hero-images/upload`, {
        method: "POST",
        body: fd,
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        const txt = await r.text();
        console.error("Hero upload error:", txt);
        throw new Error(txt);
      }
      const newImg = await r.json();
      setHeroImages((prev) => [newImg, ...prev]);
      setHeroFile(null);
      setHeroPreview(null);
      setHeroAlt("");
      setHeroOrder(0);
      if (heroInputRef.current) heroInputRef.current.value = "";
      toast("✅ Hero image uploaded!");
    } catch (err) {
      toast(`❌ Upload failed: ${err.message}`);
    }
  };

  const deleteHeroImage = async (id) => {
    if (!window.confirm("Delete this hero image?")) return;
    const token = getToken();
    await fetch(`${BASE}/hero-images/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    setHeroImages((prev) => prev.filter((i) => i._id !== id));
    toast("🗑️ Deleted");
  };

  /* ── CORE VALUES: CRUD ────────────────────────────────────────────────────── */
  const saveCoreValue = async () => {
    const token = getToken();
    const method = cvEditing ? "PUT" : "POST";
    const url = cvEditing ? `${BASE}/core-values/${cvEditing}` : `${BASE}/core-values`;
    const r = await fetch(url, {
      method,
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(cvForm),
    });
    if (!r.ok) { toast("❌ Save failed"); return; }
    const updated = await r.json();
    if (cvEditing) {
      setCoreValues((prev) => prev.map((v) => v._id === cvEditing ? updated : v));
    } else {
      setCoreValues((prev) => [...prev, updated]);
    }
    setCvForm({ icon: "🚀", title: "", description: "", order: 0 });
    setCvEditing(null);
    toast("✅ Core value saved!");
  };

  const deleteCoreValue = async (id) => {
    if (!window.confirm("Delete this value?")) return;
    const token = getToken();
    await fetch(`${BASE}/core-values/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    setCoreValues((prev) => prev.filter((v) => v._id !== id));
    toast("🗑️ Deleted");
  };

  const startEditCV = (val) => {
    setCvForm({ icon: val.icon, title: val.title, description: val.description, order: val.order });
    setCvEditing(val._id);
  };

  /* ── HIGHLIGHTS: CRUD ────────────────────────────────────────────────────── */
  const saveHighlight = async () => {
    if (!hlForm.title || !hlForm.text) {
      toast("❌ Title and Description are required");
      return;
    }
    const token = getToken();
    const fd = new FormData();
    fd.append("title", hlForm.title);
    fd.append("text", hlForm.text);
    fd.append("order", hlForm.order);
    if (hlFile) fd.append("file", hlFile);
    const method = hlEditing ? "PUT" : "POST";
    const url = hlEditing ? `${BASE}/highlights/${hlEditing}` : `${BASE}/highlights`;
    const r = await fetch(url, { method, body: fd, headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) { 
      const txt = await r.text();
      toast(`❌ Save failed: ${txt}`); 
      return; 
    }
    const updated = await r.json();
    if (hlEditing) {
      setHighlights((prev) => prev.map((h) => h._id === hlEditing ? updated : h));
    } else {
      setHighlights((prev) => [...prev, updated]);
    }
    setHlForm({ title: "", text: "", order: 0 });
    setHlFile(null);
    setHlPreview(null);
    setHlEditing(null);
    if (hlInputRef.current) hlInputRef.current.value = "";
    toast("✅ Highlight saved!");
  };

  const deleteHighlight = async (id) => {
    if (!window.confirm("Delete this highlight?")) return;
    const token = getToken();
    await fetch(`${BASE}/highlights/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    setHighlights((prev) => prev.filter((h) => h._id !== id));
    toast("🗑️ Deleted");
  };

  const startEditHL = (h) => {
    setHlForm({ title: h.title, text: h.text, order: h.order });
    setHlEditing(h._id);
  };

  /* ── FACILITIES: CRUD ────────────────────────────────────────────────────── */
  const saveFacility = async () => {
    if (!facForm.title || !facForm.tag || !facForm.points) {
      toast("❌ Title, Tag, and Bullet Points are required");
      return;
    }
    const token = getToken();
    const fd = new FormData();
    fd.append("title", facForm.title);
    fd.append("tag", facForm.tag);
    fd.append("points", facForm.points);
    fd.append("order", facForm.order);
    if (facFile) fd.append("file", facFile);
    const method = facEditing ? "PUT" : "POST";
    const url = facEditing ? `${BASE}/facilities/${facEditing}` : `${BASE}/facilities`;
    const r = await fetch(url, { method, body: fd, headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) { 
      const txt = await r.text();
      toast(`❌ Save failed: ${txt}`); 
      return; 
    }
    const updated = await r.json();
    if (facEditing) {
      setFacilities((prev) => prev.map((f) => f._id === facEditing ? updated : f));
    } else {
      setFacilities((prev) => [...prev, updated]);
    }
    setFacForm({ title: "", tag: "", points: "", order: 0 });
    setFacFile(null);
    setFacPreview(null);
    setFacEditing(null);
    if (facInputRef.current) facInputRef.current.value = "";
    toast("✅ Facility saved!");
  };

  const deleteFacility = async (id) => {
    if (!window.confirm("Delete this facility?")) return;
    const token = getToken();
    await fetch(`${BASE}/facilities/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    setFacilities((prev) => prev.filter((f) => f._id !== id));
    toast("🗑️ Deleted");
  };

  const startEditFac = (f) => {
    setFacForm({ title: f.title, tag: f.tag, points: f.points.join("\n"), order: f.order });
    setFacEditing(f._id);
  };

  /* ── DGCA CERTS: upload/delete ───────────────────────────────────────────── */
  const uploadDgca = async () => {
    if (!dgcaFile) return;
    const token = getToken();
    const fd = new FormData();
    fd.append("file", dgcaFile);
    fd.append("alt", dgcaAlt);
    const r = await fetch(`${BASE}/dgca-certs/upload`, {
      method: "POST",
      body: fd,
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!r.ok) { 
        const txt = await r.text();
        toast(`❌ Upload failed: ${txt}`); 
        return; 
    }
    const newCert = await r.json();
    setDgcaCerts((prev) => [newCert, ...prev]);
    setDgcaFile(null);
    setDgcaPreview(null);
    setDgcaAlt("");
    if (dgcaInputRef.current) dgcaInputRef.current.value = "";
    toast("✅ Certificate uploaded!");
  };

  const deleteDgca = async (id) => {
    if (!window.confirm("Delete this certificate?")) return;
    const token = getToken();
    await fetch(`${BASE}/dgca-certs/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    setDgcaCerts((prev) => prev.filter((c) => c._id !== id));
    toast("🗑️ Deleted");
  };

  /* ── PAGE CONFIG: save ───────────────────────────────────────────────────── */
  const saveConfig = async () => {
    setConfigSaving(true);
    const token = getToken();
    const r = await fetch(`${BASE}/config`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(config),
    });
    setConfigSaving(false);
    if (!r.ok) { toast("❌ Save failed"); return; }
    toast("✅ Page config saved!");
  };

  /* ── FILE PICKER helpers ──────────────────────────────────────────────────── */
  const pickFile = (setFile, setPreview) => (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  /* ── TABS ────────────────────────────────────────────────────────────────── */
  const TABS = [
    { id: "hero", label: "🖼️ Hero Images", color: "#6366f1" },
    { id: "corevalues", label: "💡 Core Values", color: "#ec4899" },
    { id: "highlights", label: "🌟 Highlights", color: "#f59e0b" },
    { id: "facilities", label: "🏫 Facilities", color: "#10b981" },
    { id: "dgca", label: "🏅 DGCA Certs", color: "#3b82f6" },
    { id: "config", label: "⚙️ Page Text", color: "#8b5cf6" },
  ];

  if (loading)
    return (
      <div className="admin-list-container">
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "80vh", gap: "20px" }}>
          <div className="loading-spinner" />
          <p style={{ fontWeight: 700, color: "#475569" }}>⏳ Loading About Us Manager...</p>
        </div>
      </div>
    );

  return (
    <div className="admin-list-container">
      {/* Header */}
      <header className="admin-list-header">
        <div className="header-left-group">
          <img src="/logo.png" alt="FlyTutor" className="admin-header-logo" />
          <h1 className="admin-header-title">About Us — Content Manager</h1>
        </div>
        <div className="module-badge" style={{ background: "#6366f1" }}>About Page</div>
      </header>

      {/* Status toast */}
      {status && (
        <div className="aa-toast">
          {status}
        </div>
      )}

      <div className="admin-list-content">
        {/* Modern Segmented Tab Bar */}
        <div className="aa-tab-bar-wrapper">
          <div className="aa-tab-bar">
            {TABS.map((t) => (
              <button
                key={t.id}
                className={`aa-tab ${activeTab === t.id ? "active" : ""}`}
                style={{ "--tab-color": t.color }}
                onClick={() => setActiveTab(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="admin-table-wrapper" style={{ padding: "40px" }}>

          {/* ═══ HERO IMAGES TAB ═══════════════════════════════════════════════ */}
          {activeTab === "hero" && (
            <div>
              <div className="aa-tab-title">Hero Slider Images <span className="aa-count">{heroImages.length}</span></div>
              <p className="aa-desc">These images appear in the rotating slider on the About Us hero section.</p>

              {/* Upload Form */}
              <div className="aa-form-card">
                <h4>Upload New Hero Image</h4>
                <div className="aa-form-row">
                  <label>
                    Image File
                    <input ref={heroInputRef} type="file" accept="image/*" onChange={pickFile(setHeroFile, setHeroPreview)} />
                  </label>
                  <label>
                    Alt Text
                    <input type="text" value={heroAlt} onChange={(e) => setHeroAlt(e.target.value)} placeholder="e.g. Drone Training" />
                  </label>
                  <label>
                    Order (0=first)
                    <input type="number" value={heroOrder} onChange={(e) => setHeroOrder(e.target.value)} min={0} />
                  </label>
                </div>
                {heroPreview && (
                  <div className="aa-preview-row">
                    <img src={heroPreview} alt="preview" className="aa-preview-img" />
                    <button className="btn-primary-elite" onClick={uploadHeroImage}>
                      <Plus size={16} /> Upload Image
                    </button>
                  </div>
                )}
                {heroFile && !heroPreview && (
                  <button className="btn-primary-elite" onClick={uploadHeroImage}>
                    <Plus size={16} /> Upload Image
                  </button>
                )}
              </div>

              {/* Grid */}
              <div className="aa-image-grid">
                {heroImages.length === 0 && <div className="aa-empty">No hero images yet. Upload one above.</div>}
                {heroImages.map((img) => (
                  <div key={img._id} className="aa-image-card">
                    <img src={`${API_BASE_URL}${img.url}`} alt={img.alt} />
                    <div className="aa-image-meta">
                      <span className="aa-image-alt">{img.alt || "No alt"}</span>
                      <span className="aa-image-order">Order: {img.order}</span>
                    </div>
                    <button className="aa-del-btn" onClick={() => deleteHeroImage(img._id)}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ═══ CORE VALUES TAB ════════════════════════════════════════════════ */}
          {activeTab === "corevalues" && (
            <div>
              <div className="aa-tab-title">Core Values <span className="aa-count">{coreValues.length}</span></div>
              <p className="aa-desc">The 4 value cards shown in the "Our Core Values" section.</p>

              <div className="aa-form-card">
                <h4>{cvEditing ? "Edit Core Value" : "Add Core Value"}</h4>
                <div className="aa-form-row">
                  <label>
                    Emoji Icon
                    <input type="text" value={cvForm.icon} onChange={(e) => setCvForm((f) => ({ ...f, icon: e.target.value }))} placeholder="🚀" />
                  </label>
                  <label>
                    Title
                    <input type="text" value={cvForm.title} onChange={(e) => setCvForm((f) => ({ ...f, title: e.target.value }))} placeholder="Innovation" />
                  </label>
                  <label>
                    Order
                    <input type="number" value={cvForm.order} onChange={(e) => setCvForm((f) => ({ ...f, order: e.target.value }))} min={0} />
                  </label>
                </div>
                <label style={{ marginBottom: "20px", display: "block" }}>
                  Description
                  <textarea rows={3} value={cvForm.description} onChange={(e) => setCvForm((f) => ({ ...f, description: e.target.value }))} placeholder="Describe this value..." />
                </label>
                <div style={{ display: "flex", gap: "12px" }}>
                  <button className="btn-primary-elite" onClick={saveCoreValue}>
                    <Save size={15} /> {cvEditing ? "Update Value" : "Add Value"}
                  </button>
                  {cvEditing && (
                    <button className="btn-secondary-elite" onClick={() => { setCvEditing(null); setCvForm({ icon: "🚀", title: "", description: "", order: 0 }); }}>
                      <X size={15} /> Cancel
                    </button>
                  )}
                </div>
              </div>

              <div className="aa-list">
                {coreValues.length === 0 && <div className="aa-empty">No core values yet.</div>}
                {coreValues.map((val) => (
                  <div key={val._id} className="aa-list-item">
                    <span className="aa-list-icon">{val.icon}</span>
                    <div className="aa-list-text">
                      <strong>{val.title}</strong>
                      <p>{val.description}</p>
                    </div>
                    <div className="aa-list-actions">
                      <button className="action-btn-elite btn-edit" onClick={() => startEditCV(val)} title="Edit"><Edit2 size={15} /></button>
                      <button className="action-btn-elite btn-delete" onClick={() => deleteCoreValue(val._id)} title="Delete"><Trash2 size={15} /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ═══ HIGHLIGHTS TAB ═════════════════════════════════════════════════ */}
          {activeTab === "highlights" && (
            <div>
              <div className="aa-tab-title">Highlights <span className="aa-count">{highlights.length}</span></div>
              <p className="aa-desc">Cards in the "Our Highlights" grid section (Excellence, Innovation, etc.)</p>

              <div className="aa-form-card">
                <h4>{hlEditing ? "Edit Highlight" : "Add Highlight"}</h4>
                <div className="aa-form-row">
                  <label>
                    Title
                    <input type="text" value={hlForm.title} onChange={(e) => setHlForm((f) => ({ ...f, title: e.target.value }))} placeholder="Excellence" />
                  </label>
                  <label>
                    Order
                    <input type="number" value={hlForm.order} onChange={(e) => setHlForm((f) => ({ ...f, order: e.target.value }))} min={0} />
                  </label>
                </div>
                <label style={{ marginBottom: "16px", display: "block" }}>
                  Description Text
                  <textarea rows={3} value={hlForm.text} onChange={(e) => setHlForm((f) => ({ ...f, text: e.target.value }))} placeholder="Description..." />
                </label>
                <label style={{ marginBottom: "16px", display: "block" }}>
                  Card Image
                  <input ref={hlInputRef} type="file" accept="image/*" onChange={pickFile(setHlFile, setHlPreview)} />
                </label>
                {hlPreview && <img src={hlPreview} alt="preview" className="aa-preview-img" style={{ marginBottom: "16px" }} />}
                <div style={{ display: "flex", gap: "12px" }}>
                  <button className="btn-primary-elite" onClick={saveHighlight}>
                    <Save size={15} /> {hlEditing ? "Update" : "Add Highlight"}
                  </button>
                  {hlEditing && (
                    <button className="btn-secondary-elite" onClick={() => { setHlEditing(null); setHlForm({ title: "", text: "", order: 0 }); setHlFile(null); setHlPreview(null); }}>
                      <X size={15} /> Cancel
                    </button>
                  )}
                </div>
              </div>

              <div className="aa-list">
                {highlights.length === 0 && <div className="aa-empty">No highlights yet.</div>}
                {highlights.map((h) => (
                  <div key={h._id} className="aa-list-item">
                    {h.imageUrl && (
                      <img src={`${API_BASE_URL}${h.imageUrl}`} alt={h.title} className="aa-list-thumb" />
                    )}
                    <div className="aa-list-text">
                      <strong>{h.title}</strong>
                      <p>{h.text}</p>
                    </div>
                    <div className="aa-list-actions">
                      <button className="action-btn-elite btn-edit" onClick={() => startEditHL(h)}><Edit2 size={15} /></button>
                      <button className="action-btn-elite btn-delete" onClick={() => deleteHighlight(h._id)}><Trash2 size={15} /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ═══ FACILITIES TAB ═════════════════════════════════════════════════ */}
          {activeTab === "facilities" && (
            <div>
              <div className="aa-tab-title">Training Facilities <span className="aa-count">{facilities.length}</span></div>
              <p className="aa-desc">Cards in "Our Training Facilities". Each card has an image and bullet points.</p>

              <div className="aa-form-card">
                <h4>{facEditing ? "Edit Facility" : "Add Facility"}</h4>
                <div className="aa-form-row">
                  <label>
                    Title
                    <input type="text" value={facForm.title} onChange={(e) => setFacForm((f) => ({ ...f, title: e.target.value }))} placeholder="Drone Training" />
                  </label>
                  <label>
                    Tag (subtitle)
                    <input type="text" value={facForm.tag} onChange={(e) => setFacForm((f) => ({ ...f, tag: e.target.value }))} placeholder="Core UAV Program" />
                  </label>
                  <label>
                    Order
                    <input type="number" value={facForm.order} onChange={(e) => setFacForm((f) => ({ ...f, order: e.target.value }))} min={0} />
                  </label>
                </div>
                <label style={{ marginBottom: "16px", display: "block" }}>
                  Bullet Points (one per line)
                  <textarea rows={5} value={facForm.points} onChange={(e) => setFacForm((f) => ({ ...f, points: e.target.value }))} placeholder={"DGCA aligned curriculum\nBasic to advanced UAV flying\nAerial safety & airspace rules"} />
                </label>
                <label style={{ marginBottom: "16px", display: "block" }}>
                  Facility Image
                  <input ref={facInputRef} type="file" accept="image/*" onChange={pickFile(setFacFile, setFacPreview)} />
                </label>
                {facPreview && <img src={facPreview} alt="preview" className="aa-preview-img" style={{ marginBottom: "16px" }} />}
                <div style={{ display: "flex", gap: "12px" }}>
                  <button className="btn-primary-elite" onClick={saveFacility}>
                    <Save size={15} /> {facEditing ? "Update" : "Add Facility"}
                  </button>
                  {facEditing && (
                    <button className="btn-secondary-elite" onClick={() => { setFacEditing(null); setFacForm({ title: "", tag: "", points: "", order: 0 }); setFacFile(null); setFacPreview(null); }}>
                      <X size={15} /> Cancel
                    </button>
                  )}
                </div>
              </div>

              <div className="aa-list">
                {facilities.length === 0 && <div className="aa-empty">No facilities yet.</div>}
                {facilities.map((f) => (
                  <div key={f._id} className="aa-list-item">
                    {f.imageUrl && (
                      <img src={`${API_BASE_URL}${f.imageUrl}`} alt={f.title} className="aa-list-thumb" />
                    )}
                    <div className="aa-list-text">
                      <strong>{f.title}</strong>
                      <span className="aa-tag-chip">{f.tag}</span>
                      <ul style={{ margin: "6px 0 0 16px", fontSize: "0.8rem", color: "#64748b" }}>
                        {f.points.map((p, i) => <li key={i}>{p}</li>)}
                      </ul>
                    </div>
                    <div className="aa-list-actions">
                      <button className="action-btn-elite btn-edit" onClick={() => startEditFac(f)}><Edit2 size={15} /></button>
                      <button className="action-btn-elite btn-delete" onClick={() => deleteFacility(f._id)}><Trash2 size={15} /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ═══ DGCA CERTS TAB ═════════════════════════════════════════════════ */}
          {activeTab === "dgca" && (
            <div>
              <div className="aa-tab-title">DGCA Approval Certificates <span className="aa-count">{dgcaCerts.length}</span></div>
              <p className="aa-desc">Certificate images shown in the "Approved by DGCA" section.</p>

              <div className="aa-form-card">
                <h4>Upload Certificate Image</h4>
                <div className="aa-form-row">
                  <label>
                    Certificate Image
                    <input ref={dgcaInputRef} type="file" accept="image/*" onChange={pickFile(setDgcaFile, setDgcaPreview)} />
                  </label>
                  <label>
                    Alt Text
                    <input type="text" value={dgcaAlt} onChange={(e) => setDgcaAlt(e.target.value)} placeholder="Certificate of Excellence" />
                  </label>
                </div>
                {dgcaPreview && (
                  <div className="aa-preview-row">
                    <img src={dgcaPreview} alt="preview" className="aa-preview-img" />
                    <button className="btn-primary-elite" onClick={uploadDgca}>
                      <Plus size={16} /> Upload Certificate
                    </button>
                  </div>
                )}
              </div>

              <div className="aa-image-grid">
                {dgcaCerts.length === 0 && <div className="aa-empty">No certificates uploaded yet.</div>}
                {dgcaCerts.map((cert) => (
                  <div key={cert._id} className="aa-image-card">
                    <img src={`${API_BASE_URL}${cert.imageUrl}`} alt={cert.alt} />
                    <div className="aa-image-meta">
                      <span className="aa-image-alt">{cert.alt}</span>
                    </div>
                    <button className="aa-del-btn" onClick={() => deleteDgca(cert._id)}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ═══ PAGE CONFIG TAB ════════════════════════════════════════════════ */}
          {activeTab === "config" && config && (
            <div>
              <div className="aa-tab-title">Page Text & Content Config</div>
              <p className="aa-desc">Edit the text shown across all About Us sections.</p>

              <div className="aa-config-grid">
                <Section title="Hero Section" color="#6366f1" defaultOpen>
                  <label>Hero Paragraph
                    <textarea rows={4} value={config.heroParagraph || ""} onChange={(e) => setConfig((c) => ({ ...c, heroParagraph: e.target.value }))} />
                  </label>
                </Section>

                <Section title="Feature Cards (FxFeatures)" color="#ec4899" defaultOpen>
                  {[1, 2, 3].map((n) => (
                    <div key={n} className="aa-config-group">
                      <strong>Feature {n}</strong>
                      <div className="aa-form-row" style={{ marginTop: "10px" }}>
                        <label>Icon Emoji
                          <input type="text" value={config[`fxFeature${n}Icon`] || ""} onChange={(e) => setConfig((c) => ({ ...c, [`fxFeature${n}Icon`]: e.target.value }))} />
                        </label>
                        <label>Title
                          <input type="text" value={config[`fxFeature${n}Title`] || ""} onChange={(e) => setConfig((c) => ({ ...c, [`fxFeature${n}Title`]: e.target.value }))} />
                        </label>
                      </div>
                      <label>Description
                        <textarea rows={2} value={config[`fxFeature${n}Desc`] || ""} onChange={(e) => setConfig((c) => ({ ...c, [`fxFeature${n}Desc`]: e.target.value }))} />
                      </label>
                    </div>
                  ))}
                </Section>

                <Section title="Core Values — Bottom Message" color="#f59e0b">
                  <label>Heading
                    <input type="text" value={config.coreValuesBottomHeading || ""} onChange={(e) => setConfig((c) => ({ ...c, coreValuesBottomHeading: e.target.value }))} />
                  </label>
                  <label>Paragraph
                    <textarea rows={3} value={config.coreValuesBottomParagraph || ""} onChange={(e) => setConfig((c) => ({ ...c, coreValuesBottomParagraph: e.target.value }))} />
                  </label>
                </Section>

                <Section title="DGCA Section" color="#3b82f6">
                  <label>Section Heading
                    <input type="text" value={config.dgcaSectionHeading || ""} onChange={(e) => setConfig((c) => ({ ...c, dgcaSectionHeading: e.target.value }))} />
                  </label>
                  <label>Approval Paragraph
                    <textarea rows={3} value={config.dgcaApprovalText || ""} onChange={(e) => setConfig((c) => ({ ...c, dgcaApprovalText: e.target.value }))} />
                  </label>
                </Section>

                <Section title="CTA Section" color="#10b981">
                  <label>CTA Heading
                    <input type="text" value={config.ctaHeading || ""} onChange={(e) => setConfig((c) => ({ ...c, ctaHeading: e.target.value }))} />
                  </label>
                  <label>CTA Paragraph
                    <textarea rows={3} value={config.ctaParagraph || ""} onChange={(e) => setConfig((c) => ({ ...c, ctaParagraph: e.target.value }))} />
                  </label>
                </Section>
              </div>

              <button className="btn-primary-elite" onClick={saveConfig} disabled={configSaving} style={{ marginTop: "30px", padding: "16px 40px", fontSize: "1rem" }}>
                <Save size={18} /> {configSaving ? "Saving..." : "Save All Config"}
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default AdminAbout;
